// preview_url — PURE. The address the preview frame opens.
//
// ── THE WHITE SCREEN (measured 2026-09-28) ──────────────────────────────────
//
// The frame's URL was built from two BUILD-TIME settings, `VITE_DEV_APP_SCHEME ||
// "http"` and `VITE_DEV_APP_PORT ?? "8080"`. Nobody set them for the node, so the
// production bundle shipped with `http` and `8080` frozen into it — read back out of
// the served bundle as `ps="http"` and `fs="8080"`. Inside https://app.getperimetr.com
// the frame therefore pointed at `http://<app>.getperimetr.com:8080/`, which is
//
//   • BLOCKED before it is even sent: an `<iframe>` whose src is http inside an https
//     page is "blockable" mixed content, which browsers block outright rather than
//     upgrade or warn about (MDN, Mixed content).
//   • and aimed at a port nothing serves publicly: the node listens on 22, 80, 443
//     and 4100 only.
//
// So the operator got a blank frame while the app itself answered 200 over https —
// verified directly on its real address.
//
// ── WHY THIS IS DERIVED AND NOT CONFIGURED ──────────────────────────────────
//
// The node's build step already injects the addresses it cannot know (VITE_AUTH_BASE,
// VITE_PLATFORM_BASE), and its comment already carries this exact lesson: a bundle
// built with the defaults "appellerait la machine du VISITEUR". Adding two more
// build-time settings would be the same trap one more time — a value someone must
// remember, failing silently when they don't.
//
// But this one is not unknowable. The frame's scheme MUST be the page's: http inside
// https is blocked, and https inside a plain-http dev page has no certificate. And the
// port follows from it — the production edge serves the apps on 443 with no port in the
// name, while a dev machine serves them on the static-server's port. So both come from
// where the operator already is, and there is nothing left to forget.
//
// The dev port stays a value the caller passes: it is the one part a page cannot tell
// (the interface and the apps listen on different ports in dev). It is only ever USED
// for a plain-http page.

/**
 * The URL for the preview frame.
 *
 * `pageProtocol` is `window.location.protocol` — "https:" or "http:", colon included,
 * as the DOM gives it. `devPort` is only consulted for a plain-http page.
 */
export function previewUrl(hostname: string, pageProtocol: string, devPort: string): string {
  if (!hostname) throw new Error("preview url: no hostname — this app has no address yet");
  // Anything that is not plainly http is treated as secure: a page served over a scheme
  // we do not recognise must not be handed an http frame, which is the blocked case.
  const plainHttp = pageProtocol === "http:";
  const scheme = plainHttp ? "http" : "https";
  const port = plainHttp && devPort ? `:${devPort}` : "";
  return `${scheme}://${hostname}${port}/`;
}
